/**
 * Idle Timeout Hook
 * Automatically detects user inactivity and triggers callbacks
 * Used for automatic logout after 5 minutes of idle time
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseIdleTimeoutOptions {
  timeout: number;                    // Idle timeout in milliseconds
  warningTimeout: number;             // Warning timeout in milliseconds (before idle)
  onIdle?: () => void;               // Callback when idle timeout is reached
  onWarning?: () => void;            // Callback when warning timeout is reached
  onActive?: () => void;             // Callback when user becomes active
  enabled?: boolean;                 // Enable/disable idle detection
  events?: string[];                 // Events to track for activity
  throttle?: number;                 // Throttle activity detection (milliseconds)
}

interface UseIdleTimeoutReturn {
  isIdle: boolean;                   // Is user currently idle?
  isWarning: boolean;                // Is warning currently active?
  remainingTime: number;             // Remaining time in seconds until logout
  resetTimer: () => void;            // Manually reset the idle timer
  pauseTimer: () => void;            // Pause idle detection
  resumeTimer: () => void;           // Resume idle detection
}

const DEFAULT_EVENTS = [
  'mousemove',
  'keydown',
  'keypress',
  'click',
  'scroll',
  'touchstart',
  'touchmove'
];

const DEFAULT_THROTTLE = 1000; // 1 second

export function useIdleTimeout(options: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const {
    timeout,
    warningTimeout,
    onIdle,
    onWarning,
    onActive,
    enabled = true,
    events = DEFAULT_EVENTS,
    throttle = DEFAULT_THROTTLE
  } = options;

  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(Math.floor(timeout / 1000));
  const [isPaused, setIsPaused] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store callbacks in refs to avoid useEffect dependency issues
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);
  const onActiveRef = useRef(onActive);

  // Update refs when callbacks change
  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarningRef.current = onWarning;
    onActiveRef.current = onActive;
  }, [onIdle, onWarning, onActive]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
  }, []);

  // Start countdown interval
  const startCountdown = useCallback(() => {
    // Clear existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, Math.floor((timeout - elapsed) / 1000));
      setRemainingTime(remaining);

      if (remaining === 0) {
        clearInterval(countdownIntervalRef.current!);
        countdownIntervalRef.current = null;
      }
    }, 1000);
  }, [timeout]);

  // Reset idle timer
  const resetTimer = useCallback(() => {
    if (isPaused || !enabled) {
      return;
    }

    clearTimers();
    lastActivityRef.current = Date.now();
    setRemainingTime(Math.floor(timeout / 1000));

    // If was idle or warning, mark as active
    if (isIdle || isWarning) {
      setIsIdle(false);
      setIsWarning(false);
      onActiveRef.current?.();
    }

    // Set warning timeout
    const warningMs = timeout - warningTimeout;
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarning(true);
      onWarningRef.current?.();
      startCountdown();
    }, warningMs);

    // Set idle timeout
    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
      setIsWarning(false);
      onIdleRef.current?.();
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, timeout);
  }, [enabled, isPaused, isIdle, isWarning, timeout, warningTimeout, clearTimers, startCountdown]);

  // Throttled activity handler
  const handleActivity = useCallback((_event?: Event) => {
    if (isPaused || !enabled) {
      return;
    }

    // Throttle activity events
    if (throttleTimeoutRef.current) {
      return;
    }

    throttleTimeoutRef.current = setTimeout(() => {
      throttleTimeoutRef.current = null;
    }, throttle);

    resetTimer();
  }, [enabled, isPaused, throttle, resetTimer]);

  // Pause timer
  const pauseTimer = useCallback(() => {
    setIsPaused(true);
    clearTimers();
  }, [clearTimers]);

  // Resume timer
  const resumeTimer = useCallback(() => {
    setIsPaused(false);
    resetTimer();
  }, [resetTimer]);

  // Store events in ref to avoid array comparison in useEffect
  const eventsRef = useRef(events);
  
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  // Set up event listeners
  useEffect(() => {
    const currentEvents = eventsRef.current;

    if (!enabled || isPaused) {
      clearTimers();
      return;
    }

    // Initial timer start
    resetTimer();

    // Add event listeners
    currentEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      clearTimers();
      currentEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isPaused]);

  return {
    isIdle,
    isWarning,
    remainingTime,
    resetTimer,
    pauseTimer,
    resumeTimer
  };
}

export default useIdleTimeout;
